import DropdownList from "@/components/ui/dropdown/list";
import { locations } from "@/constants/location"; // this is dummy data
import useOutsideAlert from "@/lib/hooks/useOutsideAlert";
import { getFilteredData } from "@/lib/utils/data";
import classNames from "classnames";
import { X } from "lucide-react";
import {
  ChangeEvent,
  FC,
  FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { FiSearch } from "react-icons/fi";
import { IoMicOutline } from "react-icons/io5";

interface HeaderSearchType {
  className?: string;
  placeholder?: string;
  optionsData?: any[];
  onOptionSelect?: (option: any) => void;
  onSearch?: (searchTerm: string) => void;
}

const HeaderSearch: FC<HeaderSearchType> = ({
  className,
  placeholder = "Search for products, services, companies",
  optionsData = locations,
  onOptionSelect,
  onSearch,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState<string>("");
  const [focused, setFocused] = useState<boolean>(false);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);

  const handleClose = useCallback(() => {
    try {
      setFocused(false);
      setShowDropdown(false);
    } catch (error) {
      console.error();
    }
  }, []);

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    try {
      setShowDropdown(true);
      const searchValue = e?.target?.value;

      setValue(searchValue);
      if (onSearch) {
        onSearch(searchValue);
      }
    } catch (error) {
      console.error();
    }
  };

  const handleSelect = (option: any) => {
    try {
      setValue(option?.title);
      if (onOptionSelect) {
        onOptionSelect(option);
      }
      setShowDropdown(false);
      inputRef?.current?.focus?.();
    } catch (error) {
      console.error();
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    try {
      e?.preventDefault();

      if (onSearch) {
        onSearch(value);
      }
    } catch (error) {
      console.error();
    }
  };

  useEffect(() => {
    return () => {
      handleClose();
    };
  }, [handleClose]);

  useOutsideAlert(containerRef, handleClose);

  const filteredMenu = getFilteredData({
    data: optionsData,
    compareValue: value,
    compareParams: ["title", "code"],
  });

  const finalData = Boolean(value) ? filteredMenu : optionsData;
  const isFocused = Boolean(value) || focused;

  return (
    <div
      ref={containerRef}
      className={classNames(
        "md:relative md:w-full md:h-full z-0 py-[1.315rem] px-0 transition-all overflow-hidden md:overflow-visible",
        isFocused &&
          "fixed w-screen h-screen top-0 left-0 bg-white md:bg-inherit transition-all px-6 md:px-0 !flex flex-col items-start [&>*]:w-full ",
        className
      )}
    >
      <div className="flex items-center gap-2">
        <form
          onSubmit={handleSubmit}
          className={classNames(
            "flex-1 min-h-min w-full flex items-center gap-1.5 border bg-white border-slate-400 rounded-full overflow-hidden p-0.5"
          )}
        >
          <button className="sm:hidden w-8 flex items-center justify-center text-stone-700 hover:text-font hover:drop-shadow-md">
            <FiSearch size={16} />
          </button>
          <input
            ref={inputRef}
            id="search"
            type="text"
            name="search"
            className="flex-1 px-0.5 sm:ps-3 sm:pe-1.5 py-1.5 rounded-tl-full rounded-bl-full bg-transparent !outline-0 focus:!outline-0 focus-within:!outline-0 !outline-transparent !ring-0 !border-0 border-none text-sm flex items-center justify-center w-full shadow-none appearance-none"
            placeholder={placeholder}
            autoComplete="off"
            onFocus={() => {
              setFocused(true);
            }}
            onChange={(e) => handleSearch(e)}
            value={value}
          />
          {value && (
            <button
              className="text-stone-700 hover:text-font hover:drop-shadow-md flex items-center justify-center w-6"
              onClick={() => {
                setValue("");
                inputRef?.current?.focus?.();
              }}
            >
              <X size={22} />
            </button>
          )}
          <button className="text-stone-700 hover:text-font hover:drop-shadow-md flex items-center justify-center w-6">
            <IoMicOutline size={22} />
          </button>
          <button
            type="submit"
            className="hidden  bg-main text-white sm:flex items-center justify-center gap-1 h-full px-3.5 py-2 rounded-full outline-0 border-none"
          >
            <FiSearch size={16} />
            <span className="font-semibold text-sm">Search</span>
          </button>
        </form>
        {isFocused && (
          <button
            className="flex items-center justify-center md:hidden"
            onClick={() => handleClose()}
          >
            <X size={22} />
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="mt-6 md:mt-0 w-full flex-1 md:absolute z-[120] top-0 md:top-full left-0 right-0 bg-white pb-6 md:pb-0">
          <DropdownList
            data={finalData}
            className="min-w-full min-h-full w-min border-0 shadow-none md:shadow-lg"
            onSelect={(option) => handleSelect(option)}
          />
        </div>
      )}
    </div>
  );
};

export default HeaderSearch;
